import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderSession, lumpsumOrder, getSchemes } from "../../../api/bseApi";

const REDEMPTION_MODES = [
  { value: "FULL", label: "Full Redemption", allRedeem: "Y" },
  { value: "PARTIAL_AMOUNT", label: "Partial Redemption (by Amount)", allRedeem: "N" },
  { value: "PARTIAL_UNITS", label: "Partial Redemption (by Units)", allRedeem: "N" },
];

/**
 * LumpsumRedemptionScreen - BSE StAR MF Lumpsum Redemption
 * Opens only if ClientCode exists
 * Receives: ClientCode, Available Schemes, Registered Bank Accounts (from user)
 */
export default function LumpsumRedemptionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [context, setContext] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({
    schemeCd: "",
    schemeCdManual: "",
    folioNo: "",
    redemptionMode: "FULL",
    amount: "",
    units: "",
    bankAccount: "",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const state = location.state;

    if (state?.clientCode) {
      setContext({
        clientCode: state.clientCode,
        banks: state.banks || [],
      });
      setBanks(state.banks || []);
      setFetching(false);
      return;
    }

    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          const uccValid = ucc && String(ucc).trim() !== "" && String(ucc) !== "Reject";
          if (!uccValid) {
            Swal.fire("Error", "ClientCode (UCC) not available. Complete UCC creation first.", "error").then(() => navigate("/user"));
            return;
          }
          const bankList = [];
          if (user.account_number) {
            bankList.push({
              accountNumber: user.account_number,
              bankName: user.bank_name || "Bank",
              display: `${user.bank_name || "Bank"} - ${user.account_number}`,
            });
          }
          setContext({
            clientCode: String(ucc).trim(),
            banks: bankList,
          });
          setBanks(bankList);
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }

    Swal.fire("Error", "Missing ClientCode. Open from User Listing.", "error").then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, userId, navigate]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const res = await getSchemes();
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSchemes(res.data.data);
        }
      } catch (err) {
        console.warn("Scheme list load failed:", err.message);
      }
    };
    loadSchemes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (name === "redemptionMode") {
      setForm((prev) => ({ ...prev, amount: "", units: "" }));
      setErrors((prev) => ({ ...prev, amount: null, units: null }));
    }
  };

  const validate = () => {
    const err = {};
    const schemeCd = form.schemeCd || form.schemeCdManual;
    if (!schemeCd?.trim()) err.schemeCd = "Scheme selection is mandatory";
    if (!form.folioNo?.trim()) err.folioNo = "Folio No is mandatory";

    const mode = form.redemptionMode;
    if (mode === "FULL") {
      if (form.amount || form.units) err.redemptionMode = "Amount and Units must be empty for Full Redemption";
    } else if (mode === "PARTIAL_AMOUNT") {
      const amt = parseFloat(form.amount);
      if (!form.amount || amt <= 0) err.amount = "Amount is required and must be > 0";
      if (form.units) err.units = "Do not enter Units when redeeming by Amount";
    } else if (mode === "PARTIAL_UNITS") {
      const qty = parseFloat(form.units);
      if (!form.units || qty <= 0) err.units = "Units are required and must be > 0";
      if (form.amount) err.amount = "Do not enter Amount when redeeming by Units";
    }

    if (!form.bankAccount?.trim()) err.bankAccount = "Bank selection is mandatory";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const sessionRes = await getOrderSession();
      if (sessionRes.data?.status !== 1) {
        const msg = sessionRes.data?.error || sessionRes.data?.message || "Session failed";
        Swal.fire("Error", msg, "error");
        setLoading(false);
        return;
      }

      const { encryptedPassword, passKey } = sessionRes.data.data;
      const schemeCd = (form.schemeCd || form.schemeCdManual || "").trim();
      const mode = form.redemptionMode;
      const allRedeem = mode === "FULL" ? "Y" : "N";
      let amount = "";
      let qty = "";
      if (mode === "PARTIAL_AMOUNT") amount = String(parseFloat(form.amount));
      if (mode === "PARTIAL_UNITS") qty = String(parseFloat(form.units));

      const payload = {
        encryptedPassword,
        passKey,
        clientCode: context.clientCode,
        schemeCd,
        buySell: "R",
        buySellType: "FRESH",
        dptxn: "P",
        amount: amount || undefined,
        qty: qty || undefined,
        allRedeem,
        folioNo: form.folioNo.trim(),
        kycStatus: "Y",
        minRedeem: "N",
        param3: form.bankAccount.trim(),
      };

      const res = await lumpsumOrder(payload);

      if (res.data?.status === 1) {
        const orderId = res.data?.data?.orderId || res.data?.data?.transNo;
        Swal.fire({
          icon: "success",
          title: "Redemption Submitted",
          text: `Redemption request submitted successfully. Order No: ${orderId || "N/A"}`,
        });
        navigate("/user");
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "Redemption failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Redemption failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const useManualScheme = schemes.length === 0;
  const isFull = form.redemptionMode === "FULL";
  const isPartialAmount = form.redemptionMode === "PARTIAL_AMOUNT";
  const isPartialUnits = form.redemptionMode === "PARTIAL_UNITS";

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link> </li>
                <li className="breadcrumb-item active">Lumpsum Redemption</li>
              </ol>
            </nav>
            <h5>Lumpsum Redemption</h5>
            <p className="text-muted mb-0">
              Client Code: {context.clientCode}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Redemption Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Scheme *</label>
                  {useManualScheme ? (
                    <input
                      type="text"
                      className={`form-control ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCdManual"
                      value={form.schemeCdManual}
                      onChange={handleChange}
                      placeholder="Enter BSE Scheme Code"
                    />
                  ) : (
                    <select
                      className={`form-select ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCd"
                      value={form.schemeCd}
                      onChange={handleChange}
                    >
                      <option value="">Select Scheme</option>
                      {schemes.map((s) => (
                        <option key={s.schemeCd} value={s.schemeCd}>
                          {s.schemeName} ({s.schemeCd})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.schemeCd && <div className="invalid-feedback">{errors.schemeCd}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Folio No *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.folioNo ? "is-invalid" : ""}`}
                    name="folioNo"
                    value={form.folioNo}
                    onChange={handleChange}
                    placeholder="Enter Folio Number"
                  />
                  {errors.folioNo && <div className="invalid-feedback">{errors.folioNo}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Redemption Mode</label>
                  <select
                    className="form-select"
                    name="redemptionMode"
                    value={form.redemptionMode}
                    onChange={handleChange}
                  >
                    {REDEMPTION_MODES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                {isPartialAmount && (
                  <div className="col-md-6">
                    <label className="form-label">Amount (OrderVal) *</label>
                    <input
                      type="number"
                      className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                      name="amount"
                      value={form.amount}
                      onChange={handleChange}
                      placeholder="Enter redemption amount"
                      min="0.01"
                      step="0.01"
                    />
                    {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                  </div>
                )}
                {isPartialUnits && (
                  <div className="col-md-6">
                    <label className="form-label">Units (Qty) *</label>
                    <input
                      type="number"
                      className={`form-control ${errors.units ? "is-invalid" : ""}`}
                      name="units"
                      value={form.units}
                      onChange={handleChange}
                      placeholder="Enter units to redeem"
                      min="0.0001"
                      step="0.0001"
                    />
                    {errors.units && <div className="invalid-feedback">{errors.units}</div>}
                  </div>
                )}
                {isFull && (form.amount || form.units) && (
                  <div className="col-12">
                    <small className="text-danger">{errors.redemptionMode}</small>
                  </div>
                )}
                <div className="col-md-6">
                  <label className="form-label">Bank Account (Param3) *</label>
                  {banks.length > 0 ? (
                    <select
                      className={`form-select ${errors.bankAccount ? "is-invalid" : ""}`}
                      name="bankAccount"
                      value={form.bankAccount}
                      onChange={handleChange}
                    >
                      <option value="">Select Bank Account</option>
                      {banks.map((b, i) => (
                        <option key={i} value={b.accountNumber}>
                          {b.display || b.accountNumber}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className={`form-control ${errors.bankAccount ? "is-invalid" : ""}`}
                      name="bankAccount"
                      value={form.bankAccount}
                      onChange={handleChange}
                      placeholder="Enter registered bank account number"
                    />
                  )}
                  {errors.bankAccount && <div className="invalid-feedback">{errors.bankAccount}</div>}
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Redemption"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
